import React, { useEffect, useState } from 'react';
import { getErrorMessage } from '../services/_apiBase';
import { useNavigate } from 'react-router-dom';
import { fetchTenants, requestSuperuserHandoff } from '../services/superuserApi';
import { useAuth } from '../context/AuthContext';

interface TenantInfo {
    id: string;
    display_name: string;
    wg_peer_ip: string | null;
    active: number;
}

const TenantSelector: React.FC = () => {
    const { selectTenant, token } = useAuth();
    const navigate = useNavigate();
    const [tenants, setTenants] = useState<TenantInfo[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        fetchTenants<TenantInfo>()
            .then(setTenants)
            .catch(err => setError(getErrorMessage(err, 'Kunde inte hämta kunderna')))
            .finally(() => setLoading(false));
    }, []);

    const handleSelect = (tenantId: string) => {
        const parts = window.location.hostname.split('.');
        if (parts.length > 1 && token) {
            // Production: get a one-time code from the backend (keeps JWT out of URL),
            // then redirect to the tenant subdomain with ?suCode=<opaque>.
            requestSuperuserHandoff(token).then(({ code }) => {
                const targetHost = [tenantId, ...parts.slice(1)].join('.');
                window.location.href = `https://${targetHost}/?suCode=${encodeURIComponent(code)}`;
            }).catch((err: any) => {
                const detail = getErrorMessage(err, '');
                setError(`Kunde inte byta kund. ${detail ? `(${detail})` : 'Försök igen.'}`);
            });
        } else {
            // Local dev (localhost): no subdomain to switch, stay on same origin.
            selectTenant(tenantId);
            navigate('/');
        }
    };

    return (
        <div style={{
            minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: '#f0f2f5', fontFamily: 'Arial, sans-serif', padding: '16px',
        }}>
            <div style={{
                backgroundColor: 'white', borderRadius: '10px', padding: '32px 28px',
                boxShadow: '0 4px 24px rgba(0,0,0,0.10)', width: '100%', maxWidth: '480px',
                boxSizing: 'border-box',
            }}>
                <h2 style={{ margin: '0 0 8px', color: '#1a1a2e', fontSize: '22px' }}>
                    🏢 Välj kund
                </h2>
                <p style={{ color: '#888', fontSize: '14px', marginBottom: '24px' }}>
                    Välj vilken kund du vill arbeta med
                </p>

                {loading && (
                    <div style={{ textAlign: 'center', color: '#888', padding: '24px 0' }}>
                        Hämtar kunder…
                    </div>
                )}

                {error && (
                    <div style={{
                        backgroundColor: '#f8d7da', color: '#721c24', padding: '10px 14px',
                        borderRadius: '6px', marginBottom: '16px', fontSize: '14px',
                    }}>
                        {error}
                    </div>
                )}

                {!loading && !error && tenants.map(t => (
                    <button
                        key={t.id}
                        onClick={() => handleSelect(t.id)}
                        style={{
                            display: 'block', width: '100%', padding: '14px 18px',
                            marginBottom: '10px', textAlign: 'left', cursor: 'pointer',
                            backgroundColor: '#f8f9fa', border: '1px solid #dee2e6',
                            borderRadius: '8px', fontSize: '15px', fontWeight: 600,
                            color: '#1a1a2e', transition: 'all .15s',
                        }}
                        onMouseEnter={e => {
                            e.currentTarget.style.backgroundColor = '#e8f0fe';
                            e.currentTarget.style.borderColor = '#007bff';
                        }}
                        onMouseLeave={e => {
                            e.currentTarget.style.backgroundColor = '#f8f9fa';
                            e.currentTarget.style.borderColor = '#dee2e6';
                        }}
                    >
                        <div>{t.display_name}</div>
                        <div style={{ fontSize: '12px', color: '#888', fontWeight: 400, marginTop: '2px' }}>
                            ID: {t.id}{t.wg_peer_ip ? ` · ${t.wg_peer_ip}` : ''}
                        </div>
                    </button>
                ))}

                {!loading && !error && tenants.length === 0 && (
                    <div style={{ textAlign: 'center', color: '#888', padding: '24px 0' }}>
                        Det finns inga aktiva kunder.
                    </div>
                )}
            </div>
        </div>
    );
};

export default TenantSelector;
